import { Button, Skeleton } from 'antd'
import { useLayoutEffect, useRef, useState } from 'react'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'
import type { DelayRow } from '@/Ship/Contracts/DashboardSummary'
import listStyles from '@/Ship/Templates/CrudResource/CrudResourceTemplate.module.css'

import { delayStatusOption } from '@/Containers/AssignmentSection/AssignmentContainer/Contracts/DelayStatus'
import { AssignmentResource } from '@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource'

import { formatDuration, formatReceivedAt } from '../Helpers/dashboardFormat'

import styles from './Dashboard.module.css'

interface ActiveDelaysProps {
    delays: DelayRow[]
    loading: boolean
    canResolve: boolean
    onResolve: (delay: DelayRow) => void
}

/** Ескальовані зверху, далі — ті, що чекають найдовше (без часу отримання — в кінці) */
const sortDelays = (delays: DelayRow[]): DelayRow[] => {
    const rank = (status: string) => (status === 'escalated' ? 0 : 1)
    const time = (iso: string | null) => {
        const value = iso ? Date.parse(iso) : Number.NaN
        return Number.isNaN(value) ? Number.POSITIVE_INFINITY : value
    }
    return [...delays].sort((a, b) => rank(a.status) - rank(b.status) || time(a.receivedAt) - time(b.receivedAt))
}

/**
 * Коментар водія: згорнутий до двох рядків, якщо реально не вміщується (міряємо після
 * рендера, а не рахуємо символи — на телефоні й у вузькій колонці рядок коротший).
 */
const DelayComment = ({ text }: { text: string }) => {
    const [expanded, setExpanded] = useState(false)
    const [overflows, setOverflows] = useState(false)
    const ref = useRef<HTMLParagraphElement>(null)

    useLayoutEffect(() => {
        const element = ref.current
        if (!element) return undefined
        const measure = () => {
            // У розгорнутому стані clamp знято; порівнюємо з висотою двох рядків
            const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight) || 18
            setOverflows(element.scrollHeight > lineHeight * 2 + 1)
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [text])

    return (
        <>
            <p
                ref={ref}
                className={`${cx(styles, 'delayComment')} ${expanded ? '' : cx(styles, 'delayCommentClamped')}`}
            >
                {text}
            </p>
            {overflows && (
                <button
                    type="button"
                    className={`${cx(styles, 'linkButton')} ${cx(styles, 'delayMore')}`}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}
                >
                    {expanded ? 'Згорнути' : 'Показати повністю'}
                </button>
            )}
        </>
    )
}

/**
 * Активні затримки («Отримано», «Ескальовано») у стилі блоку затримок картки задачі:
 * статус, скільки триває, водій і ТЗ, задача (відкриває картку), коментар водія,
 * «Вирішити». Ескальовані — зверху з червоною смужкою. Рядок перебудовується під ширину
 * самого блоку (container query): у вузькій бічній колонці й на телефоні — стовпчиком.
 */
export const ActiveDelays = ({ delays, loading, canResolve, onResolve }: ActiveDelaysProps) => {
    const sorted = sortDelays(delays)

    return (
        <section className={cx(listStyles, 'card')} aria-labelledby="dashboard-delays-title">
            <header className={cx(listStyles, 'header')}>
                <div className={cx(listStyles, 'headerMain')}>
                    <h2 id="dashboard-delays-title" className={cx(styles, 'sectionTitle')}>
                        Активні затримки
                    </h2>
                    <div className={cx(listStyles, 'subtitle')}>
                        {loading && delays.length === 0
                            ? ' '
                            : delays.length > 0
                              ? `${delays.length} ${pluralizeUk(delays.length, ['затримка', 'затримки', 'затримок'])} без результату · ескальовані зверху`
                              : 'Затримки зі статусом «Отримано» або «Ескальовано»'}
                    </div>
                </div>
            </header>

            {loading && delays.length === 0 ? (
                <div className={cx(styles, 'sectionBody')}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                </div>
            ) : sorted.length === 0 ? (
                <div className={cx(styles, 'empty')}>Активних затримок немає</div>
            ) : (
                <ul className={cx(styles, 'delays')}>
                    {sorted.map((delay) => {
                        const assignment = delay.assignment
                        const who = [assignment?.driverName, assignment?.vehicleLabel].filter(Boolean).join(' · ')
                        const duration = formatDuration(delay.receivedAt)
                        const receivedAt = formatReceivedAt(delay.receivedAt)
                        const comment = (delay.driverComment ?? '').trim()
                        return (
                            <li
                                key={delay.uuid}
                                className={`${cx(styles, 'delay')} ${delay.status === 'escalated' ? cx(styles, 'delayEscalated') : ''}`}
                            >
                                <StatusBadge option={delayStatusOption(delay.status)} fallbackLabel={delay.status} />
                                <div className={cx(styles, 'delayMain')}>
                                    <span className={cx(styles, 'delayWho')}>{who || 'Водій не вказаний'}</span>
                                    <span className={cx(styles, 'delayMeta')}>
                                        {assignment?.uuid ? (
                                            <button
                                                type="button"
                                                className={cx(styles, 'linkButton')}
                                                onClick={() =>
                                                    OpenResourceCardTask({
                                                        resource: AssignmentResource,
                                                        primaryFieldValue: assignment.uuid,
                                                        mode: 'view',
                                                    })
                                                }
                                            >
                                                Задача #{assignment.id ?? '—'}
                                            </button>
                                        ) : (
                                            'Задача —'
                                        )}
                                        {delay.transportRequest?.erpNumber && (
                                            <> · ERP {delay.transportRequest.erpNumber}</>
                                        )}
                                        {receivedAt && <> · отримано {receivedAt}</>}
                                    </span>
                                    {comment && <DelayComment text={comment} />}
                                </div>
                                <div className={cx(styles, 'delayAside')}>
                                    {duration && <span className={cx(styles, 'delayDuration')}>триває {duration}</span>}
                                    {canResolve && (
                                        <Button
                                            size="small"
                                            aria-label={`Вирішити затримку: ${who || 'водій не вказаний'}`}
                                            onClick={() => onResolve(delay)}
                                        >
                                            Вирішити
                                        </Button>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </section>
    )
}
